import { useLocation } from '@tanstack/react-router'
import { ShieldCheck, Sparkles, Zap } from 'lucide-react'
import type * as React from 'react'
import { useEffect, useState } from 'react'

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Link,
  SpreadsheetBackground,
  TextField,
} from '@/components'
import { useCheckout } from '@/modules/account'
import {
  ACCOUNT_TYPES,
  AccountTypeToggle,
  FirmNameField,
  LegalFooter,
  OtpForm,
  PaymentTrust,
  type Plan,
  PlanInformation,
  PlansSelector,
  SubmitPlan,
  singleProjectPlan,
  TermsCheckbox,
  useAuthMutating,
  useAuthMutations,
  useFetchPlans,
  usePlans,
} from '@/modules/auth'
import { useForm } from '@/stores'
import { cn } from '@/utils'

const VALUE_PROPS = [
  {
    icon: Zap,
    title: 'Weeks of analysis in minutes',
    body: 'Upload statements from any bank. Every transaction is pulled, categorized, and ready to work.',
  },
  {
    icon: Sparkles,
    title: 'Court-ready reports',
    body: 'Forensic accounting without the consultant fees or the wait. Ask questions, get charts and exhibits back.',
  },
  {
    icon: ShieldCheck,
    title: 'Numbers you can stand behind',
    body: 'Self-healing reconciliation balances each statement to its own printed totals before you export.',
  },
]

export const SignUpPage = () => {
  const location = useLocation()
  const searchParams = new URL(location.url).searchParams
  const { createAccountCheckout } = useCheckout()
  const [error, setError] = useState<string | null>(null)
  useFetchPlans()
  const plans = usePlans()

  const { signUp } = useAuthMutations()
  const isAuthMutating = useAuthMutating()
  const { data, errors, setFieldValue } = useForm()
  const [verifyCode, setVerifyCode] = useState(false)

  const selectedPriceId = data?.priceId as string | undefined
  const selectedPlan = plans.find((plan) => plan.priceId === selectedPriceId)

  const accountType = (data?.accountType as string) ?? ACCOUNT_TYPES.firm
  const isIndividual = accountType === ACCOUNT_TYPES.individual
  const accountName = isIndividual
    ? `${(data?.firstName as string) ?? ''} ${(data?.lastName as string) ?? ''}`.trim()
    : (data?.accountName as string)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await signUp({
        email: data?.email as string,
        metadata: {
          firstName: data?.firstName as string,
          lastName: data?.lastName as string,
        },
      })
      setVerifyCode(true)
    } catch (e) {
      const error = e as Error
      setError(error.message)
    }
  }

  const handleVerifiedCode = () => {
    if (selectedPriceId && !searchParams.get('priceId')) {
      searchParams.set('priceId', selectedPriceId)
    }
    if (accountName && !searchParams.get('name')) {
      searchParams.set('name', accountName)
    }

    createAccountCheckout({
      priceId: selectedPriceId as string,
      name: accountName,
      searchParams,
    })
  }

  useEffect(() => {
    const priceId = searchParams.get('priceId')
    if (priceId) {
      setFieldValue('priceId', priceId)
    }
  }, [searchParams.get('priceId')])

  useEffect(() => {
    if (!selectedPriceId && plans.length > 0) {
      setFieldValue('priceId', singleProjectPlan(plans)?.priceId)
    }
  }, [plans, selectedPriceId])

  return (
    <div className="min-h-screen bg-background px-4 sm:px-6 lg:px-8 py-8 sm:py-10 relative overflow-hidden">
      <SpreadsheetBackground />

      <div className="relative z-10 mx-auto w-full max-w-5xl xl:max-w-6xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/logo.svg" alt="CounselPro" className="h-8 sm:h-9 w-auto" />
          <h1 className="mt-6 text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-balance">
            Turn weeks of financial analysis into minutes
          </h1>
          <p className="mt-3 max-w-2xl text-sm sm:text-base text-muted-foreground text-pretty">
            Pay a flat fee for a single case that you can bill straight to your
            client. Running analysis across many matters? Firm-wide yearly plans
            are available.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 xl:gap-10 lg:items-start">
          <div className="lg:col-span-3 space-y-6">
            {selectedPlan && <PlanInformation plan={selectedPlan as Plan} />}

            <div className="hidden lg:grid grid-cols-1 gap-4 pt-2">
              {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
                <div key={title} className="flex items-start gap-3">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <div>
                    <h3 className="text-sm font-semibold tracking-tight">
                      {title}
                    </h3>
                    <p className="text-sm text-muted-foreground text-pretty">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Card className="lg:col-span-2 lg:sticky lg:top-8">
            <CardHeader className="space-y-1 pb-4">
              <h2 className="text-xl font-semibold tracking-tight text-center">
                Create your account
              </h2>
            </CardHeader>

            {verifyCode && (
              <OtpForm
                email={data?.email as string}
                onCodeVerified={handleVerifiedCode}
                onReturn={() => setVerifyCode(false)}
              />
            )}

            <CardContent className={cn('px-4 sm:px-6', verifyCode && 'hidden')}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <PlansSelector
                  value={data?.priceId as string}
                  error={errors?.priceId}
                  setFieldValue={setFieldValue}
                />
                <AccountTypeToggle
                  value={accountType}
                  setFieldValue={setFieldValue}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <TextField
                    label="First Name"
                    id="first-name"
                    placeholder="Type your first name"
                    error={errors?.firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('firstName', e.target.value)
                    }
                    required
                  />

                  <TextField
                    label="Last Name"
                    id="last-name"
                    placeholder="Type your last name"
                    error={errors?.lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('lastName', e.target.value)
                    }
                    required
                  />
                </div>

                {!isIndividual && (
                  <FirmNameField
                    error={errors?.accountName}
                    setFieldValue={setFieldValue}
                  />
                )}

                <TextField
                  label="Email Address"
                  id="email"
                  placeholder="Type your email address"
                  error={errors?.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('email', e.target.value)
                  }
                  helpText="You will receive a 6-digit code to confirm your email address."
                  autoComplete="email"
                  required
                />

                <TermsCheckbox
                  value={data?.terms as boolean}
                  setFieldValue={setFieldValue}
                  isMutating={isAuthMutating}
                  className="pt-2"
                />

                <SubmitPlan
                  agreedToTerms={data?.terms as boolean}
                  isMutating={isAuthMutating}
                >
                  Sign up
                </SubmitPlan>
                {error && <p className="text-red-500 text-sm">{error}</p>}

                <PaymentTrust />
              </form>
            </CardContent>

            <CardFooter className="flex flex-col space-y-3 pt-2 px-4 sm:px-6">
              <div className="relative w-full">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">
                    Existing user
                  </span>
                </div>
              </div>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">
                  Already have an account?{' '}
                </span>
                <Link to="/login">Sign in</Link>
              </div>
            </CardFooter>
          </Card>
        </div>

        <LegalFooter />
      </div>
    </div>
  )
}
